import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { rolesAPI, permissionsAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Check, X, Save, RotateCcw, AlertTriangle, Search, Info, Compass,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

/**
 * Roles & Permissions — single-matrix design (May 15, 2026 v30).
 *
 * Rows  = permissions, grouped by module (with sub-headers).
 * Cols  = roles (Super Admin column is read-only/locked).
 * Cells = clickable ✓/✗ to toggle. Edits are local until Save.
 *
 * This replaces the per-role card view: comparison is now built-in (just look
 * across the row), and the matrix is far less confusing on a sensitive topic.
 *
 * Edit access is Super Admin only (platform-level governance).
 */
export const RolesPage = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [landingOptions, setLandingOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // pending[roleId] = Set<permCode> of edited perm set for that role (null if no changes).
  const [pending, setPending] = useState({});
  // pendingLanding[roleId] = string path of edited default_landing.
  const [pendingLanding, setPendingLanding] = useState({});
  const [saving, setSaving] = useState(false);

  const isEditor = Boolean(user?.is_super_admin);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes, landingRes] = await Promise.all([
        rolesAPI.list(),
        permissionsAPI.list(),
        rolesAPI.landingOptions(),
      ]);
      setRoles(rolesRes.data || []);
      setPermissions(permsRes.data || []);
      setLandingOptions(landingRes.data || []);
    } catch {
      toast.error('Failed to load roles & permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const groupedPermissions = useMemo(() => {
    const filtered = search.trim()
      ? permissions.filter(p =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.code.toLowerCase().includes(search.toLowerCase()) ||
          (p.module || '').toLowerCase().includes(search.toLowerCase()))
      : permissions;
    const out = {};
    filtered.forEach(p => {
      const mod = p.module || 'other';
      if (!out[mod]) out[mod] = [];
      out[mod].push(p);
    });
    return out;
  }, [permissions, search]);

  // Hard route guard (declared AFTER all hooks so React hook order stays stable)
  if (user && !user.is_super_admin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="roles-access-restricted">
        <Card className="max-w-lg w-full">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
              <div>
                <CardTitle className="text-lg">Access Restricted</CardTitle>
                <CardDescription>This page is reserved for the Platform Super Admin.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="text-sm text-slate-600 space-y-3">
            <p>Roles &amp; Permissions is a platform-level governance feature managed centrally by Klick Niramaya HMS.</p>
            <Button asChild className="bg-emerald-600 hover:bg-emerald-700"><a href="/dashboard">Go to Dashboard</a></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getRolePerms = (role) => pending[role.id] || new Set(role.permissions || []);
  const getRoleLanding = (role) => (
    pendingLanding[role.id] !== undefined ? pendingLanding[role.id] : (role.default_landing || '')
  );

  const isDirty = (role) => {
    const cur = pending[role.id];
    const lp = pendingLanding[role.id];
    if (lp !== undefined && lp !== (role.default_landing || '')) return true;
    if (!cur) return false;
    const saved = new Set(role.permissions || []);
    if (cur.size !== saved.size) return true;
    for (const c of cur) if (!saved.has(c)) return true;
    return false;
  };

  const dirtyRoles = roles.filter(isDirty);

  const toggleCell = (role, permCode) => {
    if (!isEditor || role.code === 'super_admin') {
      if (role.code === 'super_admin') toast.info('Super Admin is locked — full access by design.');
      return;
    }
    const next = new Set(getRolePerms(role));
    if (next.has(permCode)) next.delete(permCode); else next.add(permCode);
    setPending(p => ({ ...p, [role.id]: next }));
  };

  const setLanding = (role, path) => {
    if (!isEditor) return;
    setPendingLanding(prev => ({ ...prev, [role.id]: path }));
  };

  const resetAll = () => { setPending({}); setPendingLanding({}); };

  const saveAll = async () => {
    setSaving(true);
    try {
      await Promise.all(dirtyRoles.map(r => {
        const payload = {};
        if (pending[r.id]) payload.permissions = Array.from(pending[r.id]);
        if (pendingLanding[r.id] !== undefined) payload.default_landing = pendingLanding[r.id] || null;
        return rolesAPI.update(r.id, payload);
      }));
      toast.success(`Saved ${dirtyRoles.length} role(s). Changes apply on each user's next login.`);
      // Update local state
      setRoles(rs => rs.map(r => {
        if (!isDirty(r)) return r;
        const next = { ...r };
        if (pending[r.id]) next.permissions = Array.from(pending[r.id]);
        if (pendingLanding[r.id] !== undefined) next.default_landing = pendingLanding[r.id] || null;
        return next;
      }));
      setPending({});
      setPendingLanding({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Super Admin always first column; sort other roles alphabetically by name.
  const orderedRoles = [...roles].sort((a, b) => {
    if (a.code === 'super_admin') return -1;
    if (b.code === 'super_admin') return 1;
    return a.name.localeCompare(b.name);
  });

  // Per-column header color — helps visual differentiation between roles in a wide matrix.
  const COL_HUE = ['text-emerald-600', 'text-indigo-600', 'text-rose-600', 'text-amber-600',
                   'text-cyan-600', 'text-fuchsia-600', 'text-lime-600', 'text-blue-600', 'text-orange-600'];

  return (
    <div className="space-y-6 pb-24" data-testid="roles-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Roles &amp; Permissions
        </h1>
        <p className="mt-1 text-slate-600">
          Single matrix: rows = permissions, columns = roles. Tap a cell to toggle. Compare any two roles at a glance.
        </p>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex gap-3" data-testid="roles-edit-banner">
        <Info className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900 leading-relaxed">
          <strong>Super Admin · Platform Governance.</strong> Changes here update role definitions for <strong>every hospital</strong> on Klick Niramaya HMS and are audit-logged. Apply on each user's next login. <strong>Super Admin</strong> column is locked.
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search permission name / code / module…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-10"
          data-testid="perm-search"
        />
      </div>

      {/* Matrix */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="perm-matrix">
            <thead className="bg-slate-50 sticky top-0 z-30">
              <tr className="border-b border-slate-200">
                <th className="text-left px-4 py-3 font-semibold text-slate-700 min-w-[280px] sticky left-0 bg-slate-50 z-40">
                  Permission
                </th>
                {orderedRoles.map((r, i) => (
                  <th
                    key={r.id}
                    className={`text-center px-3 py-3 font-semibold ${COL_HUE[i % COL_HUE.length]} whitespace-nowrap`}
                    data-testid={`col-header-${r.code}`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span>{r.name}</span>
                      {r.code === 'super_admin' && (
                        <span className="text-[10px] uppercase tracking-wider text-amber-600">Locked</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* v34 — Default Landing Dashboard row. Per-role dropdown for the
                  path the user is redirected to immediately after login. */}
              <tr className="border-b-2 border-emerald-100 bg-emerald-50/30">
                <td className="px-4 py-3 sticky left-0 bg-emerald-50/30 z-10">
                  <div className="flex items-center gap-2">
                    <Compass className="h-4 w-4 text-emerald-600 shrink-0" />
                    <div>
                      <div className="font-semibold text-slate-800 text-sm">Default Landing Dashboard</div>
                      <div className="text-[11px] text-slate-500">Where users land right after login</div>
                    </div>
                  </div>
                </td>
                {orderedRoles.map((r) => {
                  const value = getRoleLanding(r) || '/dashboard';
                  const dirty = pendingLanding[r.id] !== undefined && pendingLanding[r.id] !== (r.default_landing || '');
                  const locked = !isEditor || r.code === 'super_admin';
                  return (
                    <td key={r.id} className="px-2 py-2 text-center" data-testid={`landing-cell-${r.code}`}>
                      <Select
                        value={value}
                        onValueChange={(v) => setLanding(r, v)}
                        disabled={locked}
                      >
                        <SelectTrigger
                          className={`h-8 min-w-[170px] text-xs ${dirty ? 'ring-2 ring-amber-400 border-amber-400' : ''}`}
                          data-testid={`landing-select-${r.code}`}
                        >
                          <SelectValue placeholder="Default" />
                        </SelectTrigger>
                        <SelectContent>
                          {landingOptions.map(o => (
                            <SelectItem key={o.path} value={o.path}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  );
                })}
              </tr>
              {Object.entries(groupedPermissions).map(([mod, perms]) => (
                <RoleMatrixModuleRows
                  key={mod}
                  mod={mod}
                  perms={perms}
                  roles={orderedRoles}
                  getRolePerms={getRolePerms}
                  toggleCell={toggleCell}
                  isEditor={isEditor}
                  pending={pending}
                />
              ))}
              {Object.keys(groupedPermissions).length === 0 && (
                <tr>
                  <td colSpan={orderedRoles.length + 1} className="px-4 py-10 text-center text-slate-400 italic">
                    No permissions match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Floating save bar — only when there are pending edits */}
      {dirtyRoles.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-emerald-900 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4"
             data-testid="floating-save-bar">
          <div className="text-sm">
            <strong>{dirtyRoles.length}</strong> role{dirtyRoles.length === 1 ? '' : 's'} have unsaved changes
            <span className="ml-2 text-emerald-300">· {dirtyRoles.map(r => r.name).join(' · ')}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={resetAll} disabled={saving}
                  className="text-emerald-200 hover:text-white hover:bg-emerald-800"
                  data-testid="floating-reset-btn">
            <RotateCcw className="h-3 w-3 mr-1" /> Reset
          </Button>
          <Button onClick={saveAll} disabled={saving} size="sm"
                  className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                  data-testid="floating-save-btn">
            <Save className="h-3 w-3 mr-1" />
            {saving ? 'Saving…' : `Save ${dirtyRoles.length} role${dirtyRoles.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * One module's rows in the matrix. Pulled out as a sub-component so the parent's
 * pending state changes only re-render the row whose cell was clicked (cheap perf win).
 */
const RoleMatrixModuleRows = ({ mod, perms, roles, getRolePerms, toggleCell, isEditor, pending }) => (
  <>
    <tr className="bg-slate-50/70 border-b border-slate-200">
      <td colSpan={roles.length + 1} className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-500 sticky left-0 bg-slate-50/70">
        {mod}
      </td>
    </tr>
    {perms.map(p => (
      <tr key={p.code} className="border-b border-slate-100 hover:bg-emerald-50/30" data-testid={`row-${p.code}`}>
        <td className="px-4 py-2 sticky left-0 bg-white">
          <div className="text-slate-800 font-medium">{p.name}</div>
          <div className="text-[11px] text-slate-400 font-mono">{p.code}</div>
        </td>
        {roles.map(r => {
          const granted = getRolePerms(r).has(p.code);
          const isLocked = r.code === 'super_admin';
          const isPendingEdit = pending[r.id] && pending[r.id].has(p.code) !== (new Set(r.permissions || [])).has(p.code);
          const clickable = isEditor && !isLocked;
          return (
            <td key={r.id} className="text-center px-3 py-1.5">
              <button
                type="button"
                onClick={() => clickable && toggleCell(r, p.code)}
                disabled={!clickable}
                title={clickable ? `Click to ${granted ? 'remove' : 'grant'} ${p.name} for ${r.name}` : p.name}
                data-testid={`cell-${r.code}-${p.code}`}
                className={`inline-flex items-center justify-center h-7 w-7 rounded-md transition ${
                  granted
                    ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                    : 'text-slate-300 hover:bg-slate-100'
                } ${clickable ? 'cursor-pointer' : 'cursor-default opacity-70'} ${
                  isPendingEdit ? 'ring-2 ring-amber-400 ring-offset-1' : ''
                }`}
              >
                {granted ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
              </button>
            </td>
          );
        })}
      </tr>
    ))}
  </>
);

export default RolesPage;
